import { Plus, User } from "lucide-react";

import { Button } from "js/components/ui/button";
import { Label } from "js/components/ui/label";
import { RadioGroup, RadioGroupItem } from "js/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "js/components/ui/select";
import { Separator } from "js/components/ui/separator";

import { useAppSelector } from "../../../../redux/hooks";
import { formatCurrency } from "../../../../utils/utils";
import { StepComponentProps } from "../types";

export const Step2TenantSelection = ({
  formData,
  updateFormData,
  isAnimating,
  tenantsDirectory = {},
  naturalPersons = [],
  legalPersons = [],
  tenantsLoading = false,
  tenantsError = null,
  transitionToTenantRegistration,
}: StepComponentProps) => {
  const { organizationData } = useAppSelector(
    (state) => state.entities.organization,
  );
  const webCheckIncluded =
    (organizationData as any)?.web_check_included ?? false;
  const webCheckPrice = formData.tenantType === "moral" ? 250 : 150;
  const webCheckLabel =
    formData.tenantType === "moral"
      ? "Web Check para Empresa y Representante"
      : "Web Check para Inquilino";

  const webCheckSelection: "yes" | "no" | undefined =
    formData.webCheckRequired === null ||
    formData.webCheckRequired === undefined
      ? undefined
      : formData.webCheckRequired
        ? "yes"
        : "no";
  const webCheckValue = webCheckSelection ?? "";

  const handleWebCheckChange = (value: string) => {
    if (value !== "yes" && value !== "no") return;
    updateFormData("webCheckRequired", value === "yes");
  };

  // Determine which tenant list to use based on selected tenant type
  const apiTenants =
    formData.tenantType === "fisica" ? naturalPersons : legalPersons;

  // Also include locally created tenants from tenantsDirectory (for backward compatibility)
  const localTenants = Object.entries(tenantsDirectory).map(([id, tenant]) => ({
    value: id,
    label: tenant.nombre,
    fields: {
      id,
      name: tenant.nombre.split(" ")[0] || "",
      first_surname: tenant.nombre.split(" ")[1] || "",
      second_surname: tenant.nombre.split(" ")[2] || "",
      email: tenant.email,
      phone_number: tenant.telefono.split(" ")[1] || tenant.telefono,
      dial_code: tenant.telefono.split(" ")[0] || "",
      legal_type: formData.tenantType === "fisica" ? "Natural" : "Legal",
      legal_entity_name: formData.tenantType === "moral" ? tenant.nombre : null,
    } as any,
  }));

  // Combine API tenants with local tenants
  const currentTenants = [...apiTenants, ...localTenants];

  // Handle tenant selection with full tenant data
  const handleTenantSelect = (tenantId: string) => {
    const selectedTenant = currentTenants.find(
      (tenant) => tenant.value === tenantId,
    );
    if (selectedTenant) {
      updateFormData("tenant", tenantId);
      updateFormData(
        "tenantName",
        `${selectedTenant.fields.name} ${selectedTenant.fields.first_surname} ${selectedTenant.fields.second_surname || ""}`.trim(),
      );
      updateFormData(
        "tenantPhone",
        `${selectedTenant.fields.dial_code} ${selectedTenant.fields.phone_number}`,
      );
      updateFormData("tenantEmail", selectedTenant.fields.email);
    }
  };

  return (
    <div
      className={`space-y-8 transition-all duration-500 ${isAnimating ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-3 pb-4">
          <User className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">
            Selección de Inquilino
          </h3>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">
            Tipo de persona
          </Label>
          <RadioGroup
            value={formData.tenantType}
            onValueChange={(value) => {
              updateFormData("tenantType", value);
              // Clear tenant selection when changing type
              updateFormData("tenant", "");
              updateFormData("tenantName", "");
              updateFormData("tenantPhone", "");
              updateFormData("tenantEmail", "");
            }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            <label
              htmlFor="persona-fisica"
              className={`relative flex cursor-pointer rounded-md border p-3 transition-all hover:bg-gray-50 ${
                formData.tenantType === "fisica"
                  ? "border-blue-500 bg-blue-50/50"
                  : "border-gray-300"
              }`}
            >
              <RadioGroupItem
                value="fisica"
                id="persona-fisica"
                className="mt-0.5"
              />
              <div className="ml-2.5">
                <div className="text-sm font-medium text-gray-900">
                  Persona Física
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Individuos con RFC e identificación
                </div>
              </div>
            </label>
            <label
              htmlFor="persona-moral"
              className={`relative flex cursor-pointer rounded-md border p-3 transition-all hover:bg-gray-50 ${
                formData.tenantType === "moral"
                  ? "border-blue-500 bg-blue-50/50"
                  : "border-gray-300"
              }`}
            >
              <RadioGroupItem
                value="moral"
                id="persona-moral"
                className="mt-0.5"
              />
              <div className="ml-2.5">
                <div className="text-sm font-medium text-gray-900">
                  Persona Moral
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Empresas o entidades legales
                </div>
              </div>
            </label>
          </RadioGroup>
        </div>
        <div>
          <Label
            htmlFor="tenant"
            className="text-sm font-medium text-gray-700 mb-2 block"
          >
            Inquilino *
          </Label>
          <div className="flex gap-3">
            <Select
              value={formData.tenant}
              onValueChange={handleTenantSelect}
              disabled={tenantsLoading}
            >
              <SelectTrigger className="flex-1 h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900">
                <SelectValue
                  placeholder={
                    tenantsLoading
                      ? "Cargando inquilinos..."
                      : currentTenants.length === 0
                        ? "No hay inquilinos disponibles"
                        : "Selecciona un inquilino"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {tenantsError ? (
                  <SelectItem value="" disabled>
                    Error al cargar inquilinos
                  </SelectItem>
                ) : (
                  currentTenants.map((tenant) => (
                    <SelectItem key={tenant.value} value={tenant.value}>
                      {tenant.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              className="border-gray-900 text-gray-900 hover:bg-[#0f5ef7] hover:text-white hover:border-[#0f5ef7]"
              onClick={transitionToTenantRegistration}
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear
            </Button>
          </div>
        </div>
      </div>
      <Separator className="my-8" />
      <div className="space-y-4">
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-900">
              Web Check - Análisis de Riesgo Digital
            </p>
            <p className="text-xs text-gray-600">
              Obtén un reporte con todos los links de noticias a nivel global
              que mencionen a tu inquilino.
            </p>
            <p className="text-xs text-gray-600">
              <span className="font-semibold text-gray-700">Costo:</span>{" "}
              {webCheckIncluded
                ? "Incluido"
                : `${formatCurrency(webCheckPrice)} MXN`}
            </p>
          </div>
          <RadioGroup
            value={webCheckValue}
            onValueChange={handleWebCheckChange}
            className="mt-4 space-y-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="web-check-yes" />
              <Label htmlFor="web-check-yes" className="text-sm text-gray-700">
                {`Sí, agregar ${webCheckLabel}.`}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="web-check-no" />
              <Label htmlFor="web-check-no" className="text-sm text-gray-700">
                No por ahora.
              </Label>
            </div>
          </RadioGroup>
        </div>
      </div>
      <Separator className="my-8" />
      <div className="space-y-6">
        <h4 className="text-base font-semibold text-gray-900">
          Información del Inquilino
        </h4>
        {formData.tenant ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Nombre completo
                </dt>
                <dd className="text-sm text-gray-900 font-medium">
                  {formData.tenantName || "-"}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Teléfono
                </dt>
                <dd className="text-sm text-gray-900 font-medium">
                  {formData.tenantPhone || "-"}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Email
                </dt>
                <dd className="text-sm text-gray-900 font-medium">
                  {formData.tenantEmail || "-"}
                </dd>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-center text-gray-500 text-sm py-2">
              Selecciona un inquilino para ver su información
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
